import React, {Component} from 'react';
import {
    Paper,
    Typography,
    Box,
    Grid,
    TextField,
    Button,
    Skeleton,
    Avatar,
    Divider,
    IconButton,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Alert,
    Collapse
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Badge as BadgeIcon,
    ContactPhone as ContactPhoneIcon,
    PhotoCamera as PhotoCameraIcon,
    Verified as VerifiedIcon,
    AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import CryptoJS from "crypto-js";
import {KEY} from "../../../dummyData";

class PersonalInfoTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            editMode: false,
            photoDialogOpen: false,
            showSuccessAlert: false,
            profileData: {
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                secondaryPhone: "", // Add this line
                nationalId: "",
                emergencyContact: "",
                emergencyName: "",
                profileImage: null,
                verifiedID: false
            },
            formData: {
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                secondaryPhone: "", // Add this line
                nationalId: "",
                emergencyContact: "",
                emergencyName: ""
            },
            errors: {
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                emergencyContact: ""
            },
            tempProfileImage: null
        };

        // Create refs for file input
        this.fileInputRef = React.createRef();
    }

    componentDidMount() {
        // Simulate fetching user data
        this.fetchUserData();
    }

// Update the fetchUserData method in your PersonalInfoTab class component
    fetchUserData = () => {
        try {
            // Get data from localStorage
            let details = CryptoJS.AES.decrypt(
                localStorage.getItem("tenant_details"),
                KEY
            );

            let originalText = details.toString(CryptoJS.enc.Utf8);

            // Parse tenant details
            let tenantDetails = new Map(Object.entries(JSON.parse(originalText)));
             console.log(tenantDetails)

            // Create emergency contact name
            const emergencyName = tenantDetails.get("next_of_kin_first_name") && tenantDetails.get("next_of_kin_last_name")
                ? `${tenantDetails.get("next_of_kin_first_name")} ${tenantDetails.get("next_of_kin_last_name")}`
                : "";

            // Set profile data with the correct tenant details
            this.setState({
                loading: false,
                profileData: {
                    firstName: tenantDetails.get("first_name") || "",
                    lastName: tenantDetails.get("last_name") || "",
                    email: tenantDetails.get("email_address") || "",
                    phone: tenantDetails.get("phone_number") || "",
                    secondaryPhone: tenantDetails.get("secondary_phone_number") || "",
                    nationalId: tenantDetails.get("national_id") || "",
                    emergencyContact: tenantDetails.get("next_of_kin_contacts") || "",
                    emergencyName: emergencyName,
                    profileImage: tenantDetails.get("tenant_photo") || null,
                    verifiedID: tenantDetails.get("id_verification_status") !== "pending" || false,
                },
                formData: {
                    firstName: tenantDetails.get("first_name") || "",
                    lastName: tenantDetails.get("last_name") || "",
                    email: tenantDetails.get("email_address") || "",
                    phone: tenantDetails.get("phone_number") || "",
                    secondaryPhone: tenantDetails.get("secondary_phone_number") || "",
                    nationalId: tenantDetails.get("national_id") || "",
                    emergencyContact: tenantDetails.get("next_of_kin_contacts") || "",
                    emergencyName: emergencyName
                }
            });
        } catch (error) {
            console.error("Error loading profile data:", error);
            this.setState({
                loading: false,
                // You can set an error state here if needed
            });
        }
    };

    // Toggle edit mode
    toggleEditMode = () => {
        const {editMode, profileData} = this.state;

        if (editMode) {
            // Validate before saving
            if (this.validateForm()) {
                this.saveChanges();
            }
        } else {
            // Enter edit mode - reset form data to current profile data
            this.setState({
                editMode: true,
                formData: {...profileData}
            });
        }
    }

    // Handle form input changes
    handleInputChange = (e) => {
        const {name, value} = e.target;

        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                [name]: value
            }
        }), () => {
            // Validate field on change
            this.validateField(name, value);
        });
    }

    // Validate a single field
    validateField = (name, value) => {
        let error = "";

        switch (name) {
            case "firstName":
            case "lastName":
                if (!value.trim()) {
                    error = `${name === "firstName" ? "First" : "Last"} name is required`;
                }
                break;

            case "email":
                if (!value.trim()) {
                    error = "Email is required";
                } else if (!/\S+@\S+\.\S+/.test(value)) {
                    error = "Email is invalid";
                }
                break;

            case "phone":
                if (!value.trim()) {
                    error = "Phone number is required";
                } else if (!/^\d{10}$/.test(value.replace(/\D/g, ''))) {
                    error = "Phone number must be 10 digits";
                }
                break;

            case "emergencyContact":
                if (value.trim() && !/^\d{10}$/.test(value.replace(/\D/g, ''))) {
                    error = "Phone number must be 10 digits";
                }
                break;

            default:
                break;
        }

        this.setState(prevState => ({
            errors: {
                ...prevState.errors,
                [name]: error
            }
        }));

        return !error;
    }

    // Validate all form fields
    validateForm = () => {
        const {formData} = this.state;
        let isValid = true;

        // Validate each field
        for (const field of ['firstName', 'lastName', 'email', 'phone']) {
            const valid = this.validateField(field, formData[field]);
            if (!valid) isValid = false;
        }

        // Validate emergency contact if provided
        if (formData.emergencyContact) {
            const valid = this.validateField('emergencyContact', formData.emergencyContact);
            if (!valid) isValid = false;
        }

        return isValid;
    }

    // Save changes
    saveChanges = () => {
        this.setState({loading: true});

        // Simulate API call to save data
        setTimeout(() => {
            const {formData, tempProfileImage} = this.state;

            // Update profile data with form data
            this.setState(prevState => ({
                loading: false,
                editMode: false,
                profileData: {
                    ...formData,
                    profileImage: tempProfileImage || prevState.profileData.profileImage
                },
                tempProfileImage: null,
                showSuccessAlert: true
            }));

            // Hide success message after 5 seconds
            setTimeout(() => {
                this.setState({showSuccessAlert: false});
            }, 5000);
        }, 1500);
    }

    // Cancel edit mode
    cancelEdit = () => {
        this.setState(prevState => ({
            editMode: false,
            formData: {...prevState.profileData},
            tempProfileImage: null,
            errors: {
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                emergencyContact: ""
            }
        }));
    }

    // Handle profile image change
    handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();

            reader.onload = (event) => {
                this.setState({
                    tempProfileImage: event.target.result
                });
            };

            reader.readAsDataURL(e.target.files[0]);
        }
    }

    // Trigger file input click
    handleProfileImageClick = () => {
        if (this.state.editMode) {
            this.fileInputRef.current.click();
        }
    }

    // Open photo dialog
    openPhotoDialog = () => {
        this.setState({photoDialogOpen: true});
    }

    // Close photo dialog
    closePhotoDialog = () => {
        this.setState({photoDialogOpen: false});
    }

    // Format phone number for display
    formatPhoneNumber = (phoneNumberString) => {
        const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return '(' + match[1] + ') ' + match[2] + '-' + match[3];
        }
        return phoneNumberString;
    }

    // Get initials for avatar
    getInitials = () => {
        const {firstName, lastName} = this.state.profileData;
        if (!firstName && !lastName) return "JD";

        const firstInitial = firstName ? firstName.charAt(0) : "";
        const lastInitial = lastName ? lastName.charAt(0) : "";

        return (firstInitial + lastInitial).toUpperCase();
    }

    render() {
        const {
            loading,
            editMode,
            profileData,
            formData,
            errors,
            tempProfileImage,
            photoDialogOpen,
            showSuccessAlert
        } = this.state;
        const {className} = this.props;

        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden'
                }}
                className={`personal-info-tab ${className || ''}`}
            >
                {/* Success Alert */}
                <Collapse in={showSuccessAlert}>
                    <Alert
                        severity="success"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => this.setState({showSuccessAlert: false})}
                            >
                                <CloseIcon fontSize="inherit"/>
                            </IconButton>
                        }
                        sx={{borderRadius: 0}}
                    >
                        Personal information updated successfully
                    </Alert>
                </Collapse>

                {/* Form Section */}
                <Box sx={{p: 3}} className="personal-info-form">
                    {/* Basic Information Section */}
                    <Box sx={{mb: 4}} className="basic-info-section">
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: '#1F2937',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                            className="section-title"
                        >
                            <PersonIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                            Basic Information
                        </Typography>

                        <Grid container spacing={3}>
                            {/* First Name */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        name="firstName"
                                        value={editMode ? formData.firstName : profileData.firstName}
                                        onChange={this.handleInputChange}
                                        disabled={!editMode}
                                        variant="outlined"
                                        error={!!errors.firstName}
                                        helperText={errors.firstName}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="first-name-field"
                                    />
                                )}
                            </Grid>

                            {/* Last Name */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        name="lastName"
                                        value={editMode ? formData.lastName : profileData.lastName}
                                        onChange={this.handleInputChange}
                                        disabled={!editMode}
                                        variant="outlined"
                                        error={!!errors.lastName}
                                        helperText={errors.lastName}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="last-name-field"
                                    />
                                )}
                            </Grid>

                            {/* National ID */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="National ID"
                                        name="nationalId"
                                        value={editMode ? formData.nationalId : profileData.nationalId}
                                        onChange={this.handleInputChange}
                                        disabled={true} // Usually ID should not be editable
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {profileData.verifiedID ? (
                                                        <Tooltip title="Verified">
                                                            <VerifiedIcon
                                                                fontSize="small"
                                                                sx={{color: '#10B981'}}
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Pending Verification">
                                                            <AccessTimeIcon
                                                                fontSize="small"
                                                                sx={{color: '#F59E0B'}}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="national-id-field"
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{my: 3}}/>

                    {/* Contact Information Section */}
                    <Box sx={{mb: 4}} className="contact-info-section">
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: '#1F2937',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                            className="section-title"
                        >
                            <PhoneIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                            Contact Information
                        </Typography>

                        <Grid container spacing={3}>

                            {/* Phone */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        name="phone"
                                        value={editMode ? formData.phone : this.formatPhoneNumber(profileData.phone)}
                                        onChange={this.handleInputChange}
                                        disabled={!editMode}
                                        variant="outlined"
                                        error={!!errors.phone}
                                        helperText={errors.phone}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Chip
                                                        label="Primary"
                                                        size="small"
                                                        sx={{
                                                            height: 24,
                                                            fontSize: '0.75rem',
                                                            backgroundColor: '#EBF5FF',
                                                            color: '#3B82F6'
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="phone-field"
                                    />
                                )}
                            </Grid>

                            {/* Secondary Phone */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Secondary Phone Number"
                                        name="secondaryPhone"
                                        value={editMode ? formData.secondaryPhone : this.formatPhoneNumber(profileData.secondaryPhone)}
                                        onChange={this.handleInputChange}
                                        disabled={!editMode}
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="secondary-phone-field"
                                    />
                                )}
                            </Grid>

                            {/* Email */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        name="email"
                                        type="email"
                                        value={editMode ? formData.email : profileData.email}
                                        onChange={this.handleInputChange}
                                        disabled={!editMode}
                                        variant="outlined"
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="email-field"
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{my: 3}}/>

                    {/* Emergency Contact Section */}
                    <Box className="emergency-contact-section">
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: '#1F2937',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                            className="section-title"
                        >
                            <ContactPhoneIcon fontSize="small" sx={{color: '#3B82F6'}}/>
                            Emergency Contact
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Emergency Contact Name */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Emergency Contact Name"
                                        name="emergencyName"
                                        value={editMode ? formData.emergencyName : profileData.emergencyName}
                                        onChange={this.handleInputChange}
                                        disabled={!editMode}
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="emergency-name-field"
                                    />
                                )}
                            </Grid>

                            {/* Emergency Contact Phone */}
                            <Grid item xs={12} sm={6}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={56} sx={{borderRadius: '8px'}}/>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="Emergency Contact Phone"
                                        name="emergencyContact"
                                        value={editMode ? formData.emergencyContact : this.formatPhoneNumber(profileData.emergencyContact)}
                                        onChange={this.handleInputChange}
                                        disabled={!editMode}
                                        variant="outlined"
                                        error={!!errors.emergencyContact}
                                        helperText={errors.emergencyContact}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon sx={{color: '#9CA3AF'}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px'
                                            }
                                        }}
                                        className="emergency-phone-field"
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </Box>

                {/* Photo Dialog */}
                <Dialog
                    open={photoDialogOpen}
                    onClose={this.closePhotoDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            overflow: 'hidden'
                        }
                    }}
                    className="photo-dialog"
                >
                    <DialogTitle sx={{py: 2, px: 3, backgroundColor: '#f8fafc'}}>
                        Profile Photo
                    </DialogTitle>
                    <DialogContent sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                        <Avatar
                            src={profileData.profileImage}
                            sx={{
                                width: 200,
                                height: 200,
                                fontSize: '5rem',
                                fontWeight: 'bold',
                                bgcolor: '#3B82F6'
                            }}
                            className="large-profile-avatar"
                        >
                            {this.getInitials()}
                        </Avatar>
                    </DialogContent>
                    <DialogActions sx={{py: 2, px: 3, borderTop: '1px solid #e2e8f0'}}>
                        <Button
                            onClick={this.closePhotoDialog}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none'
                            }}
                            className="close-dialog-button"
                        >
                            Close
                        </Button>
                        {!profileData.profileImage && (
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    boxShadow: 'none'
                                }}
                                onClick={() => {
                                    this.closePhotoDialog();
                                    this.setState({editMode: true}, () => {
                                        setTimeout(() => {
                                            this.fileInputRef.current.click();
                                        }, 300);
                                    });
                                }}
                                className="add-photo-button"
                            >
                                Add Photo
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
            </Paper>
        );
    }
}

export default PersonalInfoTab;